import { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Avatar,
    Typography,
    IconButton,
    Chip,
    Divider,
    Grid,
    Tooltip,
    Paper
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ContactsIcon from '@mui/icons-material/Contacts';
import BarChartIcon from '@mui/icons-material/BarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorHandler from '@Root/ErrorHandler';
import OrganizationCardView from '@RegistriesViews/OrganizationCardView';
import LabelViewCreatedOverTime from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewCreatedOverTime';
import LabelViewUsageList from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewUsageList';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './OrganizationCardDisplay.scss';

export default function OrganizationCardDisplay(props) {
    const [imageUrl, setImageUrl] = useState('');
    const [showOrganizationCardView, setShowOrganizationCardView] = useState(false);
    const [companyOwnerNameCard, setCompanyOwnerNameCard] = useState({});
    const [labelData, setLabelData] = useState();
    const [POCNameCard, setPOCNameCard] = useState({});
    const nameCardService = new NameCardService();
    const errorHandler = new ErrorHandler();
    const className = 'organizationCardDisplay';

    useEffect(() => {
        renderData();
    }, [props]);

    const renderData = async () => {
        try {
            setLabelData({ ...props.organizationCard, registryType: 'orgCard' });
            if (!!props.organizationCard?.photo) {
                setImageUrl(URL.createObjectURL(dataURLtoFile(props.organizationCard?.photo)));
            } else {
                setImageUrl('');
            }
            if (!!props.organizationCard?.poc && props.organizationCard?.poc !== 0) {
                let POCNameCardObj = await nameCardService.getById(props.organizationCard?.poc);
                POCNameCardObj = JSON.parse(POCNameCardObj.body);
                setPOCNameCard(POCNameCardObj);
            }
            if (!!props.organizationCard?.owner && props.organizationCard?.owner !== 0) {
                let companyOwnerNameCardObj = await nameCardService.getById(props.organizationCard?.owner);
                companyOwnerNameCardObj = JSON.parse(companyOwnerNameCardObj.body);
                setCompanyOwnerNameCard(companyOwnerNameCardObj);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const toggleOrganizationCardView = () => {
        setShowOrganizationCardView(!showOrganizationCardView);
    };

    const getInitials = (name) => {
        if (!name) return 'ORG';
        const words = name.trim().split(' ');
        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    return (
        <div className={className}>
            <Box className={`${className}__container`}>
                {/* Header Card */}
                <Card className={`${className}__headerCard`} elevation={0}>
                    <CardContent className={`${className}__headerCard__content`}>
                        <Box className={`${className}__topSection`}>
                            {/* Avatar and Primary Info */}
                            <Box className={`${className}__primaryInfo`}>
                                <Avatar
                                    src={imageUrl}
                                    className={`${className}__avatar`}
                                    alt={props?.organizationCard?.name}
                                >
                                    {!imageUrl && getInitials(props?.organizationCard?.name)}
                                </Avatar>
                                <Box className={`${className}__nameSection`}>
                                    <Typography variant="h5" className={`${className}__name`}>
                                        {props?.organizationCard?.name}
                                    </Typography>
                                    {props.organizationCard?.industryType && (
                                        <Chip
                                            icon={<BusinessIcon />}
                                            label={props.organizationCard.industryType}
                                            size="small"
                                            className={`${className}__industryChip`}
                                        />
                                    )}
                                    {(props.organizationCard?.state || props.organizationCard?.country) && (
                                        <Box className={`${className}__location`}>
                                            <LocationOnIcon className={`${className}__location__icon`} />
                                            <Typography variant="body2">
                                                {[props.organizationCard?.state, props.organizationCard?.country]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Action Buttons */}
                            <Box className={`${className}__actions`}>
                                <Tooltip title="View Details">
                                    <IconButton
                                        className={`${className}__actionBtn ${className}__actionBtn--primary`}
                                        onClick={toggleOrganizationCardView}
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Analytics Overview">
                                    <IconButton
                                        className={`${className}__actionBtn ${className}__actionBtn--secondary`}
                                        onClick={props?.handleLabelAnalytics}
                                    >
                                        <BarChartIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Contact Information */}
                        {(props.organizationCard?.owner || props.organizationCard?.poc) && (
                            <>
                                <Divider className={`${className}__divider`} />
                                <Grid container spacing={2} className={`${className}__contactSection`}>
                                    {props.organizationCard?.owner && companyOwnerNameCard?.name && (
                                        <Grid item xs={12} sm={6}>
                                            <Paper className={`${className}__contactCard`} elevation={0}>
                                                <Box className={`${className}__contactCard__header`}>
                                                    <PersonIcon className={`${className}__contactCard__icon`} />
                                                    <Typography variant="caption" className={`${className}__contactCard__label`}>
                                                        Owner
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" className={`${className}__contactCard__value`}>
                                                    {companyOwnerNameCard.name}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                    {props.organizationCard?.poc && POCNameCard?.name && (
                                        <Grid item xs={12} sm={6}>
                                            <Paper className={`${className}__contactCard`} elevation={0}>
                                                <Box className={`${className}__contactCard__header`}>
                                                    <ContactsIcon className={`${className}__contactCard__icon`} />
                                                    <Typography variant="caption" className={`${className}__contactCard__label`}>
                                                        Point of Contact
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" className={`${className}__contactCard__value`}>
                                                    {POCNameCard.name}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Analytics charts */}
                {!!labelData && (
                    <Box className={`${className}__analyticsSection`}>
                        <Card className={`${className}__analyticsCard`} elevation={0}>
                            <CardContent>
                                <LabelViewCreatedOverTime labelData={labelData} />
                            </CardContent>
                        </Card>
                        <Card className={`${className}__analyticsCard`} elevation={0}>
                            <CardContent>
                                <LabelViewUsageList labelData={labelData} />
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>

            {showOrganizationCardView && (
                <OrganizationCardView
                    open={showOrganizationCardView}
                    closePopup={toggleOrganizationCardView}
                    reloadData={props.reloadData}
                    organizationCard={props.organizationCard}
                />
            )}
        </div>
    );
}
